/**
 * Resolve the Clerk frontend-API host from a publishable key.
 *
 * clerk-js is CDN-hotloaded from that host and GATES THE WHOLE DASHBOARD -- if
 * it does not arrive, every protected route is a blank wall. Measured on
 * production PostHog 2026-08-30, 7 of the 63 people who opened
 * app.overlayqa.com in 30 days hit a ChunkLoadError on exactly that origin.
 * Nothing in index.html mentions the host, so the browser only discovers it
 * after the main bundle has downloaded, parsed, and booted React; a
 * `preconnect` moves the DNS + TCP + TLS handshake to the start of that window.
 *
 * It must be DERIVED rather than hardcoded: production runs
 * clerk.overlayqa.com while local .env runs organic-mullet-67.clerk.accounts.dev,
 * so a literal in index.html would be wrong in one environment and would either
 * warm a useless connection or, worse, look correct while pointing elsewhere.
 * Clerk encodes the host in the key itself as base64 of "<host>$".
 */

/**
 * Conservative hostname shape: dot-separated labels of alphanumerics and
 * hyphens. Deliberately NOT a general URL parse.
 *
 * The result is interpolated into index.html at build time, so this is the
 * boundary that keeps a decoded payload from becoming markup. Anything that is
 * not plainly a host -- a scheme, a path, a space, a quote -- must resolve to
 * null and be dropped rather than escaped, because there is no legitimate key
 * whose payload contains those and a rejected preconnect costs only the
 * optimisation.
 */
const HOSTNAME_RE = /^[a-z0-9]([a-z0-9-]*[a-z0-9])?(\.[a-z0-9]([a-z0-9-]*[a-z0-9])?)+$/i;

/** Publishable keys are `pk_test_<payload>` or `pk_live_<payload>`. */
const PUBLISHABLE_KEY_RE = /^pk_(?:test|live)_(.+)$/;

/**
 * `atob` is global in every browser we support, in jsdom, and in Node >= 16,
 * which covers the two callers this has: the app bundle and vite.config.ts at
 * build time. Deliberately no Buffer fallback -- Buffer is not in the
 * dashboard's browser lib types, and a second decoder is a second thing that
 * can disagree with the first.
 */
function decodeBase64(payload: string): string | null {
  try {
    if (typeof atob !== 'function') return null;
    return atob(payload);
  } catch {
    return null;
  }
}

/**
 * @returns the frontend-API hostname, or null when the key is absent,
 *   malformed, or decodes to something that is not a bare hostname. Every
 *   caller must treat null as "skip the preconnect", never as a reason to fail.
 */
export function resolveClerkFrontendHost(publishableKey: string | undefined | null): string | null {
  const key = (publishableKey ?? '').trim();
  const match = PUBLISHABLE_KEY_RE.exec(key);
  if (!match) return null;

  // Clerk ships the payload unpadded; atob and Buffer both want a multiple of 4.
  const raw = match[1];
  const padded = raw + '='.repeat((4 - (raw.length % 4)) % 4);

  const decoded = decodeBase64(padded);
  if (!decoded) return null;

  // The payload is the host followed by a literal '$' terminator.
  const host = decoded.endsWith('$') ? decoded.slice(0, -1) : decoded;
  return HOSTNAME_RE.test(host) ? host : null;
}
